import { ArrowLeft, Ban, Clock } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import { twMerge } from 'tailwind-merge';
import type { WhatsappConversation } from '@/services/endpoints/whatsappInbox';
import { contactLabel, isWindowOpen, windowLeft } from '../inboxUtils';
import { useConversationUiState } from '../useConversationUiState';
import { ContactAvatar } from './ContactAvatar';

type Props = {
  conversation: WhatsappConversation;
  onBack?: VoidFunction;
};

export const ThreadHeader: FC<Props> = ({ conversation, onBack }) => {
  const { t } = useTranslation('whatsappInbox');
  const { isOpen, isClosed, setStatus, markClosed, reopen } = useConversationUiState(conversation.id);

  return (
    <header className='flex flex-wrap items-center gap-3 border-b border-primary-border px-4 py-3'>
      {onBack ? (
        <button
          type='button'
          aria-label={t('backToList')}
          className='flex size-9 items-center justify-center rounded-full text-primary-900/70 hover:bg-primary-background hover:text-primary-900 md:hidden'
          onClick={onBack}
        >
          <ArrowLeft className='size-4' />
        </button>
      ) : null}
      <ContactAvatar name={conversation.displayName} phone={conversation.contactPhone} />
      <div className='min-w-0 flex-1'>
        <p className='truncate text-[15px] font-bold text-primary-900'>{contactLabel(conversation)}</p>
        <p className='text-xs text-primary-900/60'>+{conversation.contactPhone}</p>
      </div>
      <div className='flex flex-wrap items-center gap-2'>
        <select
          value={isOpen ? 'open' : 'closed'}
          aria-label={t('conversationStatus')}
          className={twMerge(
            'cursor-pointer rounded-full border px-2.5 py-[5px] text-[11px] font-bold uppercase tracking-wide',
            isOpen ? 'border-green-200 bg-green-50 text-green-700' : 'border-primary-border bg-primary-background text-primary-900/60'
          )}
          onChange={(event) => setStatus(event.target.value === 'open' ? 'open' : 'closed')}
        >
          <option value='open'>{t('statusOpen')}</option>
          <option value='closed'>{t('statusClosed')}</option>
        </select>
        {isOpen ? (
          <Button type='button' intent='secondary' className='!px-3 !py-1.5 text-[13px]' onClick={markClosed}>
            {t('markClosed')}
          </Button>
        ) : null}
        {isClosed ? (
          <Button type='button' className='!px-3.5 !py-1.5 text-[13px]' onClick={reopen}>
            {t('reopen')}
          </Button>
        ) : null}
      </div>
    </header>
  );
};

export const ThreadBanners: FC<Props> = ({ conversation }) => {
  const { t } = useTranslation('whatsappInbox');
  const { isOpen, optedOut } = useConversationUiState(conversation.id);
  const windowOpen = isWindowOpen(conversation.windowOpenUntil);
  const left = windowLeft(conversation.windowOpenUntil);

  if (!isOpen) return null;

  if (optedOut) {
    return (
      <div className='flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700'>
        <Ban aria-hidden className='size-3.5 shrink-0' />
        <span>
          <b>{t('optOutBannerTitle')}</b> {t('optOutBannerBody')}
        </span>
      </div>
    );
  }

  if (windowOpen && left) {
    return (
      <div className='flex items-center gap-2 border-b border-green-200 bg-green-50 px-4 py-2.5 text-[13px] text-green-700'>
        <Clock aria-hidden className='size-3.5 shrink-0' />
        <span>
          <b>{t('windowOpenBannerTitle')}</b> · {t(left.unit === 'hours' ? 'windowOpenBannerHours' : 'windowOpenBannerMinutes', { count: left.count })}
        </span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2 border-b border-primary-border bg-primary-background px-4 py-2.5 text-[13px] text-primary-900/70'>
      <Clock aria-hidden className='size-3.5 shrink-0' />
      <span>
        <b>{t('windowExpiredBannerTitle')}</b> {t('windowExpiredBannerBody')}
      </span>
    </div>
  );
};
